import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import Add from '@mui/icons-material/Add'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import ListTableToolbar, { LIST_TABLE_TOOLBAR_BUTTON_SX } from '../../component/ListTableToolbar'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import DeleteAlertDialog from '../../component/DeleteAlertDialog'
import ListActionGroup from '../../component/ListActionGroup'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import { buildListSearch as buildListSearchQuery, createInitialListState, createListLinkTarget } from '../../utils/listPageState'

const DEFAULT_SEARCH_SELECT = {
    searchType: '',
}

const SEARCH_SELECT_PARAMS = {
    searchType: {
        param: 'type',
        defaultValue: DEFAULT_SEARCH_SELECT.searchType,
    },
}

const getInitialListState = (location) => {
    return createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

const buildListSearch = ({ curPage, searchQuery, searchSelect }) => {
    return buildListSearchQuery({
        curPage,
        searchQuery,
        searchSelect,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

export default function AppCountrys({ adminId, role }) {
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(getInitialListState(location))
    const initialListState = initialListStateRef.current
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [params, setParams] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const [listErrorMessage, setListErrorMessage] = useState('')
    const deleteInFlightRef = useRef(false)
    const silentListRefreshRef = useRef(false)

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/appCountry/getList', {
                page: curPage, offset: offset, adminId: adminId, role: role, searchText: searchQuery, searchSelect: searchSelect
            })

            if (data.code === 200) {
                setTotalCount(data.data.count || 0)
                setParams(data.data.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || 'App 국가 목록을 불러오지 못했습니다.'
                setTotalCount(0)
                setParams([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = 'App 국가 목록을 불러오지 못했습니다.'
            setTotalCount(0)
            setParams([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [adminId, curPage, history, role, searchQuery, searchSelect])

    useEffect(() => {
        const showSpinner = !silentListRefreshRef.current
        silentListRefreshRef.current = false
        getData(showSpinner)
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearch({ curPage, searchQuery, searchSelect })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery, searchSelect])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const updateSearchSelect = (e) => {
        const { name, value } = e.target

        if (actionLoading) {
            return
        }

        if (curPage === 1) {
            silentListRefreshRef.current = true
        }

        setCurPage(1)
        setSearchQuery((searchText || '').trim())
        setSearchSelect((prevSearchSelect) => ({
            ...prevSearchSelect,
            [name]: value
        }))
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const resetFilters = () => {
        if (actionLoading) {
            return
        }

        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({
            ...DEFAULT_SEARCH_SELECT,
        })
    }

    // 입력값과 실제 조회값을 분리해 Enter/검색 버튼 시점만 제어한다.
    const searchButtonClick = () => {
        if (actionLoading) {
            return
        }

        const trimmedSearchText = (searchText || '').trim()

        if (curPage === 1 && searchQuery === trimmedSearchText) {
            getData(false)
            return
        }

        setSearchQuery(trimmedSearchText)

        if (curPage !== 1) {
            setCurPage(1)
            return
        }

        if (searchQuery !== trimmedSearchText) {
            return
        }

        getData(false)
    }

    const handleDelete = (id) => {
        if (deleteInFlightRef.current) {
            return
        }

        deleteInFlightRef.current = true
        setActionLoading(true)

        const deleteData = async () => {
            try {
                const { data } = await API.post('/api/v1/admin/appCountry/delete', { countryId: id })

                if (data.code === 200) {
                    setOpenSnackbar(true)
                    setSeveritySnackbar('success')
                    setMessageSnackbar(data.message)

                    if (params.length === 1 && curPage > 1) {
                        setCurPage(curPage - 1)
                        return
                    }

                    await getData(false)
                } else if (data.code === 403) {
                    history.push('/login')
                } else {
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(data.message)
                }
            } catch (_error) {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar('App 국가를 삭제하지 못했습니다.')
            } finally {
                deleteInFlightRef.current = false
                setActionLoading(false)
            }
        }
        deleteData()
    }

    const hasActiveFilters = Boolean((searchQuery || '').trim())
        || String(searchSelect.searchType) !== String(DEFAULT_SEARCH_SELECT.searchType)

    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="App 국가 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            onClick={() => getData(false)}
                            disabled={actionLoading}
                        >
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters) {
            return (
                <ListEmptyState
                    title="조건에 맞는 App 국가가 없습니다."
                    description="검색어 또는 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={actionLoading} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="등록된 App 국가가 없습니다."
                description="App 국가가 등록되면 이 목록에 표시됩니다."
            />
        )
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    {/* 타이틀 */}
                    <Title>App 국가 리스트</Title>
                    {/* 검색폼 */}
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={actionLoading} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="국가코드/언어코드/국가명"
                                type="search" 
                                name="searchText"
                                value={searchText}
                                disabled={actionLoading}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={actionLoading}><SearchIcon /></IconButton>
                            <Divider sx={{ height: 28, m: 0.5, marginRight:2 }} orientation="vertical" />
                            <TextField
                                label="대륙"
                                select
                                name="searchType"
                                value={searchSelect.searchType}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={actionLoading}
                                sx={{ minWidth: 150 }}
                            >
                                <MenuItem value={''}>전체</MenuItem>
                                <MenuItem value={'asia'}>아시아</MenuItem>
                                <MenuItem value={'oceania'}>오세아니아</MenuItem>
                                <MenuItem value={'n-america'}>북아메리카</MenuItem>
                                <MenuItem value={'s-america'}>남아메리카</MenuItem>
                                <MenuItem value={'europe'}>유럽</MenuItem>
                                <MenuItem value={'africa'}>아프리카</MenuItem>
                            </TextField>
                        </ListFilterForm>

                    </ListFilterBar>
                    
                    {/* 테이블 */}
                    <ListTableToolbar
                        right={(
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<Add />}
                                size="small"
                                disabled={actionLoading}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                onClick={() => history.push(createListLinkTarget({
                                    pathname: '/new-country',
                                    search: location.search,
                                    curPage: curPage,
                                    searchText: searchQuery,
                                    searchSelect: searchSelect
                                }))}
                            >
                                등록
                            </Button>
                        )}
                    />
                    <ListTableContainer>
                    <Table size="small" sx={{ minWidth: 800 }}>
                        <caption>Total Count: {totalCount}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell>대륙</TableCell>
                                <TableCell>이미지</TableCell>
                                <TableCell>국가코드</TableCell>
                                <TableCell>언어코드</TableCell>
                                <TableCell>한국어<br/>영어<br/>현지어<br/></TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {params.length > 0 ? (
                                params.map((row) => (
                                    <TableRow key={row.countryId}>
                                        <TableCell>{row.rownum}</TableCell>
                                        <TableCell>{row.continents}</TableCell>
                                        <TableCell><img src={`${process.env.REACT_APP_AWS_S3_URL}/${row.image}`} alt="" width={50} /></TableCell>
                                        <TableCell>{row.cCode}</TableCell>
                                        <TableCell>{row.lCode}</TableCell>
                                        <TableCell className="listTableLimitText">{row.nameKr}<br/>{row.nameEn}<br/>{row.nameLo ? row.nameLo : '-'}</TableCell>
                                        <TableCell align="right">
                                            <ListActionGroup disabled={actionLoading}>
                                                <EditIcon path={'country'} id={row.countryId} search={location.search} searchText={searchQuery} searchSelect={searchSelect} curPage={curPage}/>
                                                <DeleteAlertDialog handleDelete={handleDelete} id={row.countryId}/>
                                            </ListActionGroup>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        {renderEmptyState()}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
